<template>
    <form v-if="status.detail" @submit.prevent="submitForm">
        <b-form-group :label="$t('label.name')" label-cols="3" label-align="left">
            <b-form-input v-model.trim="form.name" :state="fieldState('name')" />
            <b-form-invalid-feedback>{{ $t('message.requiredX', [$t('label.name')]) }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="$t('label.sequence')" label-cols="3" label-align="left">
            <b-form-input v-model.number="form.sequence" type="number" min="1" :placeholder="$t('label.optional')" />
            <b-form-text>{{ $t('label.automaticSequenceHint') }}</b-form-text>
        </b-form-group>

        <b-form-group :label="$t('label.code')" label-cols="3" label-align="left">
            <b-form-input v-model.trim="form.code" :state="fieldState('code')" />
            <b-form-invalid-feedback>{{ $t('message.requiredX', [$t('label.code')]) }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group :label="$t('module.documentCategory')" label-cols="3" label-align="left">
            <multiselect v-model="form.documentCategory" track-by="value" label="text"
                :placeholder="$t('label.searchSelectDocumentCategory')" :options="documentCategoryDdl" :searchable="true"
                :allow-empty="false" :loading="categoryLoading"
                :class="submitted && !$v.form.documentCategory.required ? 'border border-danger' : ''"
                @search-change="searchCategories" />
            <div v-if="submitted && !$v.form.documentCategory.required" class="invalid-feedback d-block text-right">
                {{ $t('label.categoryRequired') }}
            </div>
        </b-form-group>

        <b-form-group :label="$t('label.visibleToCompany')" label-cols="3" label-align="left">
            <multiselect v-model="form.visibleToCompany" track-by="value" label="text"
                :placeholder="$t('label.searchSelectCompanyOptional')" :options="companyDdl" :searchable="true"
                :allow-empty="true" :loading="companyLoading" :deselect-label="$t('label.clearSelection')"
                @search-change="searchCompanies" />
            <b-form-text>{{ $t('label.companyVisibilityOptionalHint') }}</b-form-text>
        </b-form-group>

        <b-form-group :label="$t('label.openDocumentIn')" label-cols="3" label-align="left">
            <b-form-radio-group v-model="form.isInternal" stacked>
                <b-form-radio :value="1">{{ $t('label.ihextarApp') }}</b-form-radio>
                <b-form-radio :value="0">{{ $t('label.externalViewerApp') }}</b-form-radio>
            </b-form-radio-group>
            <b-form-text>
                {{ $t('label.documentOpenHint') }}
            </b-form-text>
        </b-form-group>


        <b-form-group v-if="form.isInternal === 1" :label="$t('label.file')" label-cols="3" label-align="left">
            <b-form-file v-model="form.file" :state="fileState" :placeholder="$t('label.chooseDocumentFile')"
                :drop-placeholder="$t('label.dropDocumentFile')" />
            <b-form-text v-if="documentUUID && form.fileUrl">
                {{ $t('label.keepCurrentFile') }}
            </b-form-text>
            <div v-if="submitted && !form.file && !form.fileUrl" class="invalid-feedback d-block text-right">
                {{ $t('label.fileRequired') }}
            </div>
        </b-form-group>

        <b-form-group v-else :label="$t('label.externalLink')" label-cols="3" label-align="left">
            <b-form-input v-model.trim="form.link" type="url" placeholder="https://example.com/document"
                :state="fieldState('link')" />
            <b-form-invalid-feedback>{{ $t('label.externalLinkRequired') }}</b-form-invalid-feedback>
            <b-form-text>{{ $t('label.externalLinkHint') }}</b-form-text>
        </b-form-group>


        <hr class="my-3 mx-n3">
        <div class="d-flex justify-content-end">
            <b-button type="button" variant="danger" class="mr-2" @click="$emit('done')">
                {{ $t('label.cancel') }}
            </b-button>
            <b-button type="submit" variant="info" :disabled="!status.save">
                <span v-if="status.save">
                    {{ $t(documentUUID ? 'label.updateX' : 'label.createX', [$t('module.document')]) }}
                </span>
                <b-spinner v-else small />
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center p-5">
        <b-spinner variant="info" />
    </div>
</template>

<script>
import { required, requiredIf, url } from 'vuelidate/lib/validators';
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import { documentMethods, lookupMethods } from '@/state/api';
import { DOCUMENT_MODULE } from '@/state/api/document.state';

export default {
    name: 'DocumentForm',
    components: { Multiselect },
    props: {
        documentUUID: { type: String, default: '' },
        searchCriteria: { type: Object, required: true },
    },
    data() {
        return {
            submitted: false,
            companyLoading: false,
            categoryLoading: false,
            companyTimer: null,
            categoryTimer: null,
            form: {
                name: '', sequence: null, code: '', documentCategory: null,
                visibleToCompany: null, file: null, link: '', isInternal: 1,
            },
        };
    },
    validations: {
        form: {
            name: { required },
            code: { required },
            documentCategory: { required },
            link: {
                required: requiredIf(function () { return this.form.isInternal === 0; }),
                url,
            },
        },
    },
    computed: {
        ...mapState(DOCUMENT_MODULE, ['status']),
        details() {
            return this.$store.state.api.document.details || {};
        },
        companyDdl() {
            return this.$store.state.api.lookup.companyDdl || [];
        },
        documentCategoryDdl() {
            return this.$store.state.api.lookup.documentCategoryDdl || [];
        },
        fileState() {
            return this.submitted ? Boolean(this.form.file || this.form.fileUrl) : null;
        },
    },
    async created() {
        await Promise.all([this.lookupCompany(), this.lookupDocumentCategory()]);
        await this.loadDocument();
    },
    beforeDestroy() {
        clearTimeout(this.companyTimer);
        clearTimeout(this.categoryTimer);
    },
    methods: {
        ...documentMethods,
        ...lookupMethods,
        fieldState(field) {
            return this.submitted ? !this.$v.form[field].$invalid : null;
        },
        optionFor(options, uuid, record) {
            return options.find((option) => option.value === uuid) || (uuid ? {
                value: uuid,
                text: record?.name || uuid,
            } : null);
        },
        async loadDocument() {
            if (!this.documentUUID) return;
            const result = await this.getDocument({ uuid: this.documentUUID });
            if (!result) return;
            this.form = {
                ...this.form,
                ...this.details,
                isInternal: this.details.isInternal ? 1 : 0,
                file: null,
                documentCategory: this.optionFor(
                    this.documentCategoryDdl,
                    this.details.documentCategoryUUID,
                    this.details.documentCategory,
                ),
                visibleToCompany: this.optionFor(
                    this.companyDdl,
                    this.details.visibleToCompanyUUID,
                    this.details.visibleToCompany,
                ),
            };
        },
        searchCompanies(term) {
            clearTimeout(this.companyTimer);
            this.companyTimer = setTimeout(async () => {
                this.companyLoading = true;
                await this.lookupCompany(term ? [term] : []);
                this.companyLoading = false;
            }, 350);
        },
        searchCategories(term) {
            clearTimeout(this.categoryTimer);
            this.categoryTimer = setTimeout(async () => {
                this.categoryLoading = true;
                await this.lookupDocumentCategory(term ? [term] : []);
                this.categoryLoading = false;
            }, 350);
        },
        async submitForm() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid || (this.form.isInternal === 1 && !this.form.file && !this.form.fileUrl)) return;
            const data = {
                ...this.form,
                documentCategoryUUID: this.form.documentCategory.value,
                visibleToCompanyUUID: this.form.visibleToCompany?.value || null,
            };
            const action = this.documentUUID ? this.updateDocument : this.createDocument;
            const result = await action({ data, filter: this.searchCriteria });
            if (result?.success) this.$emit('done');
        },
    },
};
</script>
